import { createSlice } from '@reduxjs/toolkit';
import {
    createExpenseGroupAction,
    deleteExpenseGroupAction,
    getExpenseGroupByIdAction,
    getExpenseGroupsAction,
    paginateExpenseGroupsAction,
    updateExpenseGroupAction,
} from 'redux/action/expenseGroupAction';
import { toastText } from 'utils/utils';

const initialState: any = {
    data: [],
    total: 0,
    isLoading: false,
    isUpdateLoading: false,
    count: 0,
    error: null,
    fistTimeFetchLoading: true,
    currentPage: 1,
    totalPages: 0,
};

const ExpenseGroupSlice = createSlice({
    name: 'expenseGroups',
    initialState,
    reducers: {
        clearExpenseGroups: () => {
            return {
                ...initialState,
            };
        },
        updateLoadingHandler: (state) => {
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        // Get All Expense Groups
        builder.addCase(getExpenseGroupsAction.pending, (state) => {
            // Only set first time loading if we don't have data yet
            if (state.data.length === 0) {
                state.fistTimeFetchLoading = true;
            } else {
                state.isLoading = true;
            }
            state.error = null;
        });
        builder.addCase(getExpenseGroupsAction.fulfilled, (state, action) => {
            state.data = action.payload.data;
            state.count = action.payload.total;
            state.currentPage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.fistTimeFetchLoading = false;
            state.isLoading = false;
        });
        builder.addCase(getExpenseGroupsAction.rejected, (state, action: any) => {
            state.error = action.payload;
            state.fistTimeFetchLoading = false;
            state.isLoading = false;
        });

        // Paginate Expense Groups
        builder.addCase(paginateExpenseGroupsAction.fulfilled, (state, action) => {
            state.isLoading = false;
            state.data = action.payload;
        });

        // Get Single Expense Group by ID
        builder.addCase(getExpenseGroupByIdAction.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(getExpenseGroupByIdAction.fulfilled, (state) => {
            state.isLoading = false;
            // This is typically used for edit mode, so we don't update the main data array
        });
        builder.addCase(getExpenseGroupByIdAction.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create Expense Group
        builder.addCase(createExpenseGroupAction.pending, (state) => {
            state.isUpdateLoading = true;
            state.error = null;
        });
        builder.addCase(createExpenseGroupAction.fulfilled, (state, action) => {
            state.isUpdateLoading = false;
            state.data = [action.payload.data, ...state.data];
            state.count = state.count + 1;
            toastText(action.payload.message, 'success');
        });
        builder.addCase(createExpenseGroupAction.rejected, (state, action: any) => {
            state.isUpdateLoading = false;
            state.error = action.payload;
            toastText(action.payload?.message, 'error');
        });

        // Update Expense Group
        builder.addCase(updateExpenseGroupAction.pending, (state) => {
            state.isUpdateLoading = true;
            state.error = null;
        });
        builder.addCase(updateExpenseGroupAction.fulfilled, (state, action) => {
            state.isUpdateLoading = false;
            const updatedExpenseGroup = action.payload.data;

            state.data = state.data.map((expenseGroup: any) => {
                if (expenseGroup?.id === updatedExpenseGroup.id) {
                    return {
                        ...expenseGroup,
                        ...updatedExpenseGroup,
                    };
                }
                return expenseGroup;
            });

            toastText(action.payload.message, 'success');
        });
        builder.addCase(updateExpenseGroupAction.rejected, (state, action: any) => {
            state.isUpdateLoading = false;
            state.error = action.payload;
            toastText(action.payload?.message, 'error');
        });

        // Delete Expense Group
        builder.addCase(deleteExpenseGroupAction.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(deleteExpenseGroupAction.fulfilled, (state, action) => {
            state.isLoading = false;
            state.data = state.data.filter(
                (item: any) => item?.id !== action.payload.id
            );
            state.count = state.count - 1;
            toastText(action.payload.response.message, 'success');
        });
        builder.addCase(deleteExpenseGroupAction.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
            toastText(action.payload?.message, 'error');
        });
    },
});

export default ExpenseGroupSlice;
export const { clearExpenseGroups, updateLoadingHandler } = ExpenseGroupSlice.actions;
